<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        Skillform Leaderboard
        <i
          class="material-icons icon-16pt clickable-item"
          v-b-popover.hover.top="'View student rankings across Skillform games, exams, and flashcards.'"
        >
          info
        </i>
      </h2>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <b-tabs content-class="mt-3">
            <b-tab title="GAMES" :active="activeTab === 'games'" @click="handleTabClick('games')"> </b-tab>
            <b-tab title="EXAMS" :active="activeTab === 'exams'" @click="handleTabClick('exams')"> </b-tab>
            <b-tab title="FLASHCARDS" :active="activeTab === 'flashcards'" @click="handleTabClick('flashcards')">
            </b-tab>
          </b-tabs>

          <div class="card-header" style="padding: 1.5rem 1.25rem;">
            <div class="d-flex align-items-center" style="white-space: nowrap; gap: 1rem; margin-bottom: 1.5rem;">
              <div class="flex-grow-0">
                <form class="search-form search-form--light d-lg-inline-flex mb-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search by student name"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="students"
              v-if="students.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(rank)="data">
                <div class="d-flex align-items-center justify-content-center">
                  <span v-if="data.item.rank <= 3" class="rank-badge" :class="'rank-' + data.item.rank">
                    {{ data.item.rank }}
                  </span>
                  <span v-else>{{ data.item.rank }}</span>
                </div>
              </template>

              <template #cell(student_name)="data">
                <div class="d-flex align-items-center">
                  <b-avatar
                    :src="get(data.item, 'user.avatar_url')"
                    :text="getInitials(data.item)"
                    size="32"
                    class="mr-2"
                    variant="light"
                  ></b-avatar>
                  <strong>{{ get(data.item, 'user.first_name', '') }} {{ get(data.item, 'user.last_name', '') }}</strong>
                </div>
              </template>

              <template #cell(email)="data">
                {{ get(data.item, 'user.email', '-') }}
              </template>

              <template #cell(program)="data">
                {{ get(data.item, 'enrolled_program.title', '-') }}
              </template>

              <template #cell(score)="data">
                <b-badge variant="primary" class="score-badge">
                  {{ formatScore(data.item) }}
                </b-badge>
              </template>
            </b-table>

            <div v-if="!isLoading && !students.length" class="text-center p-4">
              <p>No students found.</p>
            </div>

            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalStudents"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="leaderboard-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, debounce } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '@/common/constants';
import Pagination from '@/components/Ui/Pagination.vue';

export default {
  components: {
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'Skillform Leaderboard',
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalStudents: 0,
      searchTerm: '',
      students: [],
      activeTab: 'games',
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    scoreField() {
      const map = {
        games: 'skillform_game_score',
        exams: 'skillform_exam_score',
        flashcards: 'skillform_flashcard_score',
      };
      return map[this.activeTab];
    },

    tableColumns() {
      const scoreLabel = {
        games: 'Points',
        exams: 'Avg Score',
        flashcards: 'Cards Studied',
      };

      return [
        { key: 'rank', label: '#', sortable: false, class: 'text-center', thStyle: { width: '60px' } },
        { key: 'student_name', label: 'Student', sortable: false },
        { key: 'email', label: 'Email', sortable: false },
        { key: 'program', label: 'Program', sortable: false },
        { key: 'score', label: scoreLabel[this.activeTab], sortable: false, class: 'text-center' },
      ];
    },
  },

  methods: {
    ...mapActions('student', ['getAllStudents']),
    get,

    getInitials(student) {
      const first = get(student, 'user.first_name', '');
      const last = get(student, 'user.last_name', '');
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    },

    formatScore(student) {
      const score = student[this.scoreField] || 0;
      return score.toLocaleString();
    },

    onPageChange(pageNum) {
      this.currentPage = pageNum;
      this.fetchStudents();
    },

    async fetchStudents() {
      this.isLoading = true;
      try {
        const params = {
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ordering: `-${this.scoreField}`,
          ...(this.searchTerm && { search: this.searchTerm }),
        };

        const response = await this.getAllStudents(params);
        const results = response.results || response.data || response;
        this.totalStudents = response.count || results.length;

        this.students = results.map((student, index) => ({
          ...student,
          rank: (this.currentPage - 1) * this.perPage + index + 1,
        }));
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to fetch leaderboard data.' });
      } finally {
        this.isLoading = false;
      }
    },

    handleTabClick(tabKey) {
      if (this.activeTab === tabKey) return;

      this.activeTab = tabKey;
      this.currentPage = 1;
      this.searchTerm = '';

      if (this.$route.query.tab !== tabKey) {
        this.$router.replace({ query: { ...this.$route.query, tab: tabKey } }).catch(() => {});
      }

      this.fetchStudents();
    },

    onSearch() {
      this.currentPage = 1;
      this.debouncedSearch();
    },

    debouncedSearch: debounce(function () {
      this.fetchStudents();
    }, 500),
  },

  async created() {
    const tabFromQuery = this.$route.query.tab;
    const validTabs = ['games', 'exams', 'flashcards'];
    if (tabFromQuery && validTabs.includes(tabFromQuery)) {
      this.activeTab = tabFromQuery;
    }

    this.fetchStudents();
  },
};
</script>

<style scoped>
.clickable-item {
  cursor: pointer;
}

::v-deep .nav-tabs {
  border-bottom: 3px solid #e5e7eb;
  padding: 0.25rem 1.5rem 0;
  background: #f9fafb;
  min-height: 64px;
  display: flex;
  align-items: flex-end;
}

::v-deep .nav-tabs .nav-link {
  border: none;
  color: #6b7280;
  font-weight: 600;
  font-size: 1.05rem;
  padding: 1.25rem 2.75rem;
  margin-bottom: -4px;
  border-bottom: 4px solid transparent;
  transition: all 0.2s ease;
  background: transparent;
  border-radius: 12px 12px 0 0;
}

::v-deep .nav-tabs .nav-link:hover {
  color: #111827;
  border-bottom-color: #d1d5db;
  background: #ffffff;
}

::v-deep .nav-tabs .nav-link.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  background: #ffffff;
  font-weight: 700;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.85rem;
  color: #fff;
}

.rank-1 {
  background: #f59e0b;
}

.rank-2 {
  background: #9ca3af;
}

.rank-3 {
  background: #b45309;
}

.score-badge {
  font-size: 0.85rem;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
}
</style>
